export enum FirmwareComparison {
  EQUAL = 0,
  GREATER_THAN = 1,
  LESS_THAN = -1,
  NOT_COMPARABLE = 99,
}

/**
 *
 * @param currentVersion
 * @param latestVersion
 * @returns if v1 > v2, return FirmwareComparison.GREATER_THAN
 *          if v1 < v2, return FirmwareComparison.LESS_THAN
 *          if v1 === v2, return FirmwareComparison.EQUAL
 *          if v1 and v2 are not comparable, return FirmwareComparison.NOT_COMPARABLE
 */
export const compareInverterFWModuleVersions = (v1: string, v2: string): FirmwareComparison => {
  const removeLeadingZeros = (str: string): string => str.replace(/^0+/, '');

  const cleanedV1 = removeLeadingZeros(v1);
  const cleanedV2 = removeLeadingZeros(v2);

  const prefix1 = cleanedV1.charAt(0);
  const prefix2 = cleanedV2.charAt(0);

  if (prefix1 !== prefix2) {
    return FirmwareComparison.NOT_COMPARABLE;
  }

  const pad = (str: string): string => str.padEnd(10, '0');
  const paddedV1 = pad(cleanedV1);
  const paddedV2 = pad(cleanedV2);

  const numericV1 = paddedV1.slice(1).split('.').map(Number);
  const numericV2 = paddedV2.slice(1).split('.').map(Number);

  for (let i = 0; i < Math.max(numericV1.length, numericV2.length); i++) {
    const n1 = numericV1[i] || 0;
    const n2 = numericV2[i] || 0;

    if (n1 > n2) {
      return FirmwareComparison.GREATER_THAN;
    } else if (n1 < n2) {
      return FirmwareComparison.LESS_THAN;
    }
  }

  return FirmwareComparison.EQUAL;
};

export const fwVersionLetter = (version: string): string => {
  const vInit = version.replace(/^0+/, '');
  return (vInit.match(/[a-zA-Z]/g)?.[0] || '').toUpperCase();
};

/**
 * If firmware1 is greater than firmware2, return FirmwareComparison.GREATER_THAN
 * If firmware1 is less than firmware2, return FirmwareComparison.LESS_THAN
 * If firmware1 is equal to firmware2, return FirmwareComparison.EQUAL
 */
export const compareFirmwareVersions = (firmware1: string, firmware2: string): FirmwareComparison => {
  // split firmware version into parts
  const parts1 = firmware1.split(/([^0-9A-Fa-f]+)/).filter(Boolean);
  const parts2 = firmware2.split(/([^0-9A-Fa-f]+)/).filter(Boolean);

  // if the number of parts is different, the versions are not comparable
  if (parts1.length !== parts2.length) {
    return FirmwareComparison.NOT_COMPARABLE;
  }

  // compare each part
  for (let i = 0; i < parts1.length; i++) {
    const part1 = parts1[i];
    const part2 = parts2[i];

    const isHex1 = /^[0-9A-Fa-f]+$/.test(part1);
    const isHex2 = /^[0-9A-Fa-f]+$/.test(part2);

    if (isHex1 && isHex2) {
      const num1 = parseInt(part1, 16);
      const num2 = parseInt(part2, 16);
      if (num1 > num2) return FirmwareComparison.GREATER_THAN;
      if (num1 < num2) return FirmwareComparison.LESS_THAN;
    } else {

      // if the part is not a number, compare the parts as strings
      if (part1 > part2) return FirmwareComparison.GREATER_THAN;
      if (part1 < part2) return FirmwareComparison.LESS_THAN;
    }
  }

  return FirmwareComparison.EQUAL;
};
